import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { addDays, formatISO } from 'date-fns';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { createSemenOptions } from '@/services/semen-service/hooks';
import { mapSemenSortModel } from '@/services/semen-service/sort';
import { SemenDto } from '@/services/semen-service/types';
import { addGridQueryParams } from '@/utils/query';

import { SemenFilters } from './types';

export const useSemenDataSource = (
  filters: SemenFilters,
  setLoading: (loading: boolean) => void,
  isSemenDataPointsEnabled: boolean = false,
) => {
  const queryClient = useQueryClient();
  const isFixCustomViews = useFeatureFlag('Fix_Custom_Views');

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      setLoading(true);

      const searchParams = new URLSearchParams();

      if (filters.cane) {
        searchParams.set('cane', `contains:${filters.cane}`);
      }
      if (filters.storageLocation?.id) {
        searchParams.set('storageLocationId', filters.storageLocation.id);
      }
      if (filters.storageLocationSite?.id) {
        searchParams.set(
          'storageLocationSiteId',
          filters.storageLocationSite.id,
        );
      }
      if (filters.sire?.id) {
        searchParams.set('sireId', filters.sire.id);
      }
      if (filters.naab) {
        searchParams.set('naab', `contains:${filters.naab}`);
      }
      if (filters.ownerCode) {
        searchParams.set('ownerCode', `contains:${filters.ownerCode}`);
        searchParams.set('ownerName', `contains:${filters.ownerCode}`);
      }
      if (filters.dateReceived) {
        searchParams.set(
          'dateReceived',
          `gte:${formatISO(filters.dateReceived, { representation: 'date' })}`,
        );
        searchParams.append(
          'dateReceived',
          `lt:${formatISO(addDays(filters.dateReceived, 1), { representation: 'date' })}`,
        );
      }
      if (filters.mayIncludeShipment !== undefined) {
        searchParams.set(
          'mayIncludeShipment',
          filters.mayIncludeShipment.toString(),
        );
      }

      const sortModel =
        params.request.sortModel.length > 0
          ? params.request.sortModel
          : (filters.order ?? []);
      const effectiveSortModel = isFixCustomViews
        ? mapSemenSortModel(sortModel)
        : sortModel;
      const { shipmentName: _shipmentName, ...filterModel } =
        (params.request.filterModel as FilterModel | null) ?? {};

      queryClient
        .fetchQuery(
          createSemenOptions(
            addGridQueryParams(
              searchParams,
              filterModel,
              effectiveSortModel,
              params.request.startRow,
              params.request.endRow,
            ),
            isSemenDataPointsEnabled,
          ),
        )
        .then((response) => {
          let rowsThisPage = response.items;

          if (filters?.mayIncludeShipment === false) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            rowsThisPage = rowsThisPage.filter((semen: SemenDto) => {
              const hasShipment =
                (semen.shipmentName && semen.shipmentName.trim() !== '') ||
                (semen.shipmentNumber && semen.shipmentNumber.trim() !== '');

              if (hasShipment) {
                return false;
              }

              if (semen.donorScheduleActive === true) {
                if (semen.donorScheduleDate) {
                  const donorScheduleDate = new Date(semen.donorScheduleDate);
                  donorScheduleDate.setHours(0, 0, 0, 0);
                  if (donorScheduleDate >= today) {
                    return true;
                  }
                }
                return false;
              }

              return true;
            });
          }

          const filteredOut = response.items.length - rowsThisPage.length;

          params.success({
            rowData: rowsThisPage,
            rowCount: response.totalCount - filteredOut,
          });
          setLoading(false);
        })
        .catch((_error) => {
          params.fail();
          setLoading(false);
        });
    },
    [
      queryClient,
      filters,
      setLoading,
      isSemenDataPointsEnabled,
      isFixCustomViews,
    ],
  );

  const dataSource: IServerSideDatasource = useMemo(
    () => ({ getRows }),
    [getRows],
  );

  return { dataSource };
};
